import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import type { Language } from '@/i18n/translations';
import { motion, AnimatePresence } from 'framer-motion';

const langLabels: Record<Language, string> = { he: 'עב', ar: 'عر', en: 'EN' };

const navKeys = [
  { key: 'nav.home' as const, path: '/' },
  { key: 'nav.about' as const, path: '/about' },
  { key: 'nav.cv' as const, path: '/cv' },
  { key: 'nav.lectures' as const, path: '/lectures' },
  { key: 'nav.media' as const, path: '/media' },
  { key: 'nav.partners' as const, path: '/partners' },
  { key: 'nav.certificates' as const, path: '/certificates' },
  { key: 'nav.good_deeds' as const, path: '/good-deeds' },
  { key: 'nav.blog' as const, path: '/blog' },
  { key: 'nav.contact' as const, path: '/contact' },
];

const Header = () => {
  const { t, lang, switchLang, localePath } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.key]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
      <div className="container mx-auto px-4 flex items-center justify-between h-16 lg:h-20">
        {/* Logo */}
        <Link to={localePath('/')} className="flex items-center gap-2">
          <img src="/images/logo.avif" alt={t('hero.title')} className="h-10 lg:h-14 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navKeys.map(({ key, path }) => (
            <Link
              key={key}
              to={localePath(path)}
              className="px-3 py-2 text-sm text-muted-foreground hover:text-primary transition-colors rounded-md hover:bg-secondary/50"
            >
              {t(key)}
            </Link>
          ))}
        </nav>

        {/* Lang switcher + mobile toggle */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-secondary/50 rounded-full p-1">
            <Globe className="w-4 h-4 text-primary mx-1" />
            {(['he', 'ar', 'en'] as Language[]).map((l) => (
              <button
                key={l}
                onClick={() => switchLang(l)}
                className={`px-2.5 py-1 text-xs rounded-full transition-all ${
                  lang === l
                    ? 'bg-primary text-primary-foreground font-semibold'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {langLabels[l]}
              </button>
            ))}
          </div>

          <button
            className="lg:hidden p-2 text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background/95 backdrop-blur-xl border-b border-border animate-in slide-in-from-top-2 duration-200">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-1">
            {navKeys.map(({ key, path }) => (
              <Link
                key={key}
                to={localePath(path)}
                onClick={() => setMobileOpen(false)}
                className="px-4 py-3 text-sm text-muted-foreground hover:text-primary hover:bg-secondary/50 rounded-md transition-colors"
              >
                {t(key)}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
